
import React from "react";
import * as ReactRouterDom from "react-router-dom";
import { Classname } from "@rivtn/noseur";
import { i18nManager } from "../../i18n";
import { CacheManager, Utils } from "../../../../../utils";
import styles from "../../assets/style.module.css";
import { IMember, ITeamWebsite } from "../../../../../services";
import LanguageDropdown from "../../../../../shared/LanguageDropdown";
import { i18nManager as globalI18nManager } from "../../../../../i18n";
import { DASHBOARD_URL, isPageVisible, pagePath, teamInitials, teamLogo, visiblePages } from "../../helpers";

export interface NavbarProps {
    basePath: string;
    cartCount: number;
    profile?: IMember;
    website: ITeamWebsite;
    onSignOut: () => void;
}

function Navbar({ website, basePath, profile, cartCount, onSignOut }: NavbarProps) {

    const labels = i18nManager.Labels;
    const authLabels = globalI18nManager.Labels.auth;
    const landingLabels = globalI18nManager.Labels.landing;
    const ddRef = React.useRef<HTMLDivElement>(null);
    const location = ReactRouterDom.useLocation();
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const [showDropdown, setShowDropdown] = React.useState(false);
    const [searchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const logo = teamLogo(website);
    const search = searchParams.toString();

    React.useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ddRef.current && !ddRef.current.contains(e.target as Node)) setShowDropdown(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    React.useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    React.useEffect(() => {
        document.body.style.overflow = mobileOpen ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [mobileOpen]);

    return (<>
        <header className={styles["nav"]}>
            <div className={styles["nav-inner"]}>
                <button className={Classname.build(styles["burger"], { [styles["open"]]: mobileOpen })} onClick={() => setMobileOpen((o) => !o)} aria-label={labels.menu} aria-expanded={mobileOpen}>
                    <span /><span /><span />
                </button>

                <ReactRouterDom.NavLink to={{ pathname: `/${basePath}`, search }} className={styles["nav-logo"]}>
                    {logo ? (<img src={logo} alt={website.team.name} />) : (<span className={styles["nav-logo-text"]}>{teamInitials(website.team.name)}</span>)}
                </ReactRouterDom.NavLink>

                <nav className={styles["nav-menu"]}>
                    {visiblePages(website).map(([page, pageSettings]) => (
                        <ReactRouterDom.NavLink end key={page} to={{ pathname: pagePath(basePath, page), search }}
                            className={({ isActive }) => Classname.build(styles["nav-link"], { [styles["active"]]: isActive })}>{pageSettings.label}</ReactRouterDom.NavLink>
                    ))}
                </nav>

                <div className={styles["nav-right"]}>
                    <span className={styles["nav-desktop"]}><LanguageDropdown /></span>
                    {(website.extra_data?.enable_visitor_account_management) && ((!profile) ? (
                        <button className={styles["nav-icon"]} onClick={() => Utils.signIn()} aria-label={authLabels.sign_in}><i className="fa fa-user" /></button>
                    ) : (<div className={styles["dropdown-wrap"]} ref={ddRef}>
                        <img className={styles["avatar"]} onClick={() => setShowDropdown((v) => !v)} src={profile.profile_picture_url} alt={profile.username} />
                        {showDropdown && (<div className={styles["dropdown"]}>
                            <span className={styles["dd-head"]}>@{profile.username}</span>
                            <a target="_blank" rel="noreferrer" className={styles["dd-item"]} href={DASHBOARD_URL}>{authLabels.dashboard}</a>
                            <a target="_blank" rel="noreferrer" className={styles["dd-item"]} href={`${DASHBOARD_URL}/teams`}>{labels.my_teams}</a>
                            <a target="_blank" rel="noreferrer" className={styles["dd-item"]} href={`${DASHBOARD_URL}/tournaments?section=REGISTERED`}>{landingLabels.my_tournaments}</a>
                            <hr className={styles["dd-divider"]} />
                            <button className={Classname.build(styles["dd-item"], styles["logout"])} onClick={() => {
                                CacheManager.clear();
                                setShowDropdown(false);
                                onSignOut();
                            }}>{authLabels.sign_out}</button>
                        </div>)}
                    </div>))}
                    {isPageVisible(website, "MERCH") && (<ReactRouterDom.NavLink to={{ pathname: pagePath(basePath, "MERCH"), search }} className={styles["nav-icon"]} aria-label={labels.shop}>
                        <i className="fa fa-shopping-bag" />
                        {cartCount > 0 && (<span className={styles["cart-bubble"]}>{cartCount}</span>)}
                    </ReactRouterDom.NavLink>)}
                </div>
            </div>
        </header>

        <div className={Classname.build(styles["drawer"], { [styles["open"]]: mobileOpen })} aria-hidden={!mobileOpen}>
            <nav className={styles["drawer-links"]}>
                {visiblePages(website).map(([page, pageSettings], i) => (
                    <ReactRouterDom.NavLink end key={page} to={{ pathname: pagePath(basePath, page), search }} style={{ transitionDelay: mobileOpen ? `${80 + i * 40}ms` : "0ms" }}
                        className={({ isActive }) => Classname.build(styles["drawer-link"], { [styles["active"]]: isActive })}>{pageSettings.label}</ReactRouterDom.NavLink>
                ))}
            </nav>
            <div className={styles["drawer-foot"]}><LanguageDropdown /></div>
        </div>
        {mobileOpen && (<div className={styles["drawer-scrim"]} onClick={() => setMobileOpen(false)} />)}
    </>);
}

export default Navbar;
